const {
  getElement,
  createElement,
  getOffsetLeft,
  getOffsetTop,
} = window.BAO.Utils

class ProductMedia extends window.BAO.CustomElement() {
  static get styles () {
    return `
      :host {
        display: block;
      }
    `
  }

  renderTemplate () {
    const template = createElement('div', {
      class: 'prd-MediaZoom',
      'data-product-media-external-el': 'wrapper',
    })
    template.insertAdjacentHTML(
      'afterbegin',
      `
      ${this.renderThumbnails()}

      ${this.renderImages()}
      
      <button type="button" class="prd-MediaZoom_Close">
        <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8.828 3.328 3.171 8.985M8.829 8.985 3.172 3.328" stroke="currentColor" stroke-linecap="round"></path>
        </svg>

        <span class="util-ScreenReaderOnly">Close</span>
      </button>
    `,
    )

    Array.from(template.querySelectorAll('[data-thumbnail]')).forEach(
      thumbnail => {
        thumbnail.addEventListener('click', () => {
          template
            .querySelector(
              `[data-item="${thumbnail.getAttribute('data-thumbnail')}"]`,
            )
            .scrollIntoView({ behavior: 'smooth' })
        })
      },
    )

    return template
  }

  renderThumbnails () {
    const template = createElement('div', {
      class: 'prd-MediaZoom_Thumbnails',
    })

    template.appendChild(this.els.thumbnails.element.cloneNode(true))

    return template.outerHTML
  }

  renderImages () {
    const template = createElement('div', {
      class: 'prd-MediaZoom_Images',
    })
    template.appendChild(this.els.medias.element.cloneNode(true))

    return template.outerHTML
  }

  static get requiredElements () {
    return ['thumbnails', 'thumbnail', 'medias', 'media']
  }

  setupListeners () {
    super.setupListeners()

    this.onThumbnailClick = this.onThumbnailClick.bind(this)
    // this.onImageHover = this.onImageHover.bind(this)
    this.onImageClick = this.onImageClick.bind(this)

    this.listeners.add(
      this.els.thumbnails.element,
      'click',
      this.onThumbnailClick,
    )
    this.querySelectorAll('.prd-MediaCarousel_Slide-image').forEach(element => {
      this.listeners.add(element, 'click', () => {
        this.showLightbox(element.dataset.slide)
      })
    })

    if (this.els.media?.elements?.length) {
      this.els.media.elements.forEach(element => {
        this.listeners.add(element, 'click', this.onImageClick)
      })
    }

    this.observeItems()
  }

  onImageClick (event) {
    const image = event.target.closest(this.els.media.selector)
    if (!image) return

    this.showLightbox(image.dataset.item)
  }

  showLightbox (index) {
    const lightboxNode = this.renderTemplate()
    document.documentElement.appendChild(lightboxNode)
    lightboxNode
      .querySelector(`.prd-Media_Item[data-item="${index}"]`)
      .scrollIntoView()

    const productCount = lightboxNode.querySelectorAll(`.prd-Media_Item`).length
    const mediaItems = lightboxNode.querySelector('.prd-Media_Items')
    const lightboxController = new AbortController()
    const signal = lightboxController.signal

    const closeLightbox = () => {
      lightboxNode.parentNode.removeChild(lightboxNode)
      lightboxController.abort()
    }

    window.addEventListener(
      'keydown',
      ev => {
        if (ev.key === 'Escape') {
          closeLightbox()
        }
      },
      {
        signal,
      },
    )

    lightboxNode
      .querySelector('.prd-MediaZoom_Close')
      .addEventListener('click', () => {
        closeLightbox()
      })

    lightboxNode.addEventListener('click', ev => {
      if (ev.target.closest('.prd-Media_Thumbnails')) return

      closeLightbox()
    })

    lightboxNode
      .querySelector('.prd-MediaZoom_Images')
      .addEventListener('scroll', ev => {
        const totalHeight = mediaItems.getBoundingClientRect().height
        const oneHeight = totalHeight / productCount
        const currentIndex =
          Math.ceil(
            (ev.target.scrollTop + window.innerHeight / 4) / oneHeight,
          ) - 1

        const currentThumb = lightboxNode.querySelector(
          `[data-thumbnail="${currentIndex}"]`,
        )
        if (currentThumb.getAttribute('aria-current') !== 'true') {
          lightboxNode
            .querySelector(`[data-thumbnail][aria-current="true"]`)
            ?.setAttribute('aria-current', 'false')
          currentThumb.setAttribute('aria-current', 'true')
        }
      })
  }

  onThumbnailClick (event) {
    if (this.querySelector('product-carousel')) return

    const thumbnail = event.target.closest(this.els.thumbnail.selector)
    if (!thumbnail) return

    const media = this.els.media?.elements?.find(
      el => thumbnail.dataset.thumbnail === el.dataset.item,
    )
    if (!media) return

    window.BAO.Utils.scrollToY(getOffsetTop(media) - this.anchorOffset)
  }

  onImageHover (event) {
    const media = event.target.closest(this.els.media.selector)
    if (!media) return

    const imageZoomElement = getElement('.rsp-Image', {
      context: media,
    })
    if (!imageZoomElement) return

    imageZoomElement.style.transformOrigin = `${event.pageX -
      getOffsetLeft(media)}px ${event.pageY - getOffsetTop(media)}px`
  }

  observeItems () {
    if (!this.els.media?.elements?.length) return

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 1,
    }

    function observerCallback (entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const thumbnailItem = Array.from(
            document.querySelectorAll(
              '.prd-Media [data-product-media-el="thumbnail"]',
            ),
          ).find(el => el.dataset.thumbnail === entry.target.dataset.item)

          thumbnailItem.setAttribute('aria-current', true)
          document
            .querySelectorAll('.prd-Media [data-product-media-el="thumbnail"]')
            .forEach(item => {
              if (item !== thumbnailItem) {
                item.setAttribute('aria-current', false)
              }
            })
        }
      })
    }

    // eslint-disable-next-line compat/compat
    const observer = new IntersectionObserver(observerCallback, observerOptions)

    this.els.media.elements.forEach(media => observer.observe(media))
  }

  get anchorOffset () {
    const headerHeight =
      document.querySelector('[data-section-type="header"]')?.offsetHeight ?? 0
    const gap = 20

    return headerHeight + gap
  }
}

if (!customElements.get('product-media')) {
  customElements.define('product-media', ProductMedia)
}
